import { useEffect } from 'react';
import type { MathTrace } from './mathTrace';

/**
 * Math modal — renders a structured trace showing how the engine got
 * to the row's status. Pure display; no edits, no decisions.
 *
 * Sections:
 *   1. Portfolio net — every PF row that contributed, signed
 *   2. DP net — grouped by inferred sub-section, each subtotalled
 *   3. SLB outstanding — pairings + unmatched first legs, if any
 *   4. Status decision — the rule that fired
 *
 * Closing: ESC key, click on backdrop, or the close button.
 */
export function MathModal({ trace, onClose }: {
  trace: MathTrace;
  onClose: () => void;
}) {
  // ESC to close — common UX, no library needed for one keystroke.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const statusColor = trace.status === 'MATCH'           ? 'var(--status-ok)'
                   :  trace.status === 'SLB_OUTSTANDING' ? 'var(--status-info)'
                   :  trace.status === 'QTY_MISMATCH'    ? 'var(--status-warn)'
                   :  'var(--text-200)';

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.55)' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="rounded-lg overflow-hidden max-h-[85vh] flex flex-col"
        style={{
          background: 'var(--bg-base)',
          width: '100%',
          maxWidth: '640px',
          color: 'var(--text-strong)',
        }}
      >
        {/* Header */}
        <div className="px-5 py-3 border-b flex items-center gap-3"
             style={{ borderColor: 'var(--glass-border)' }}>
          <div className="flex-1">
            <div className="text-sm font-semibold">
              How we got to <span style={{ color: statusColor }}>{trace.status}</span>
            </div>
            <div className="text-xs font-mono" style={{ color: 'var(--text-200)' }}>
              {trace.isin} · {trace.display_name}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-lg leading-none px-2 py-1 rounded hover:bg-axis-cloud-100"
            style={{ color: 'var(--text-200)' }}
            aria-label="Close"
          >×</button>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">

          {/* ─── 1. Portfolio net ───────────────────────────────────── */}
          <Section title="1 · Portfolio net">
            {trace.pf.rows.length === 0 ? (
              <Note>No portfolio transactions for this ISIN.</Note>
            ) : (
              <CodeTable>
                <thead>
                  <tr>
                    <Th>Date</Th><Th>Type</Th><Th align="right">Raw qty</Th><Th align="right">Signed</Th>
                  </tr>
                </thead>
                <tbody>
                  {trace.pf.rows.map(r => (
                    <tr
                      key={r.pair_id}
                      style={r.outside_window ? { opacity: 0.5 } : undefined}
                      title={r.outside_window ? 'Outside statement period — excluded from net' : undefined}
                    >
                      <Td>{fmtDate(r.date)}</Td>
                      <Td>{r.txn_type}{r.outside_window ? ' · out of window' : ''}</Td>
                      <Td align="right">{fmtQty(r.raw_qty)}</Td>
                      <Td align="right" tone={r.outside_window ? 'mute' : r.signed_qty > 0 ? 'ok' : r.signed_qty < 0 ? 'warn' : 'mute'}>
                        {r.outside_window
                          ? '0 (excl.)'
                          : r.kind === 'dividend'
                            ? '0 (div)'
                            : r.kind === 'unknown'
                              ? '?'
                              : fmtSigned(r.signed_qty)}
                      </Td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: '1px dashed var(--glass-border)' }}>
                    <Td colSpan={3} bold>portfolio_net =</Td>
                    <Td align="right" bold>{fmtSigned(trace.pf.net)}</Td>
                  </tr>
                </tbody>
              </CodeTable>
            )}
          </Section>

          {/* ─── 2. DP net ──────────────────────────────────────────── */}
          <Section title="2 · DP net">
            {trace.dp.rows.length === 0 ? (
              <Note>No DP transactions for this ISIN.</Note>
            ) : (
              <div className="space-y-3">
                {trace.dp.by_section.map(sec => (
                  <div key={sec.section}>
                    <div className="text-[11px] uppercase tracking-wider mb-1"
                         style={{ color: 'var(--text-200)' }}>
                      {sec.section} section · {sec.rows.length} row{sec.rows.length === 1 ? '' : 's'}
                    </div>
                    <CodeTable>
                      <tbody>
                        {sec.rows.map(r => (
                          <tr key={r.pair_id}>
                            <Td width="80px">{fmtDate(r.date)}</Td>
                            <Td className="truncate">{r.description || '—'}</Td>
                            <Td align="right" width="70px"
                                tone={r.signed_qty > 0 ? 'ok' : r.signed_qty < 0 ? 'warn' : 'mute'}>
                              {fmtSigned(r.signed_qty)}
                            </Td>
                          </tr>
                        ))}
                        <tr>
                          <Td colSpan={2} align="right" italic mute>subtotal:</Td>
                          <Td align="right" italic>{fmtSigned(sec.subtotal)}</Td>
                        </tr>
                      </tbody>
                    </CodeTable>
                  </div>
                ))}
                <div className="flex justify-between items-center pt-2 border-t border-dashed"
                     style={{ borderColor: 'var(--glass-border)' }}>
                  <span className="text-sm font-semibold font-mono">
                    dp_net = {trace.dp.by_section.map(s => fmtSigned(s.subtotal)).join(' + ')} =
                  </span>
                  <span className="text-sm font-semibold font-mono">{fmtSigned(trace.dp.net)}</span>
                </div>
              </div>
            )}
          </Section>

          {/* ─── 3. SLB outstanding ────────────────────────────────── */}
          <Section title="3 · SLB outstanding detection">
            {trace.slb.matched.length === 0 && trace.slb.unmatched_firsts.length === 0 ? (
              <Note>No SLB legs detected (no rows containing both "SLB" and "LEG"). slb_outstanding = 0</Note>
            ) : (
              <div className="space-y-3">
                {trace.slb.matched.length > 0 && (
                  <div>
                    <div className="text-[11px] uppercase tracking-wider mb-1"
                         style={{ color: 'var(--text-200)' }}>
                      Paired legs ({trace.slb.matched.length})
                    </div>
                    <CodeTable>
                      <tbody>
                        {trace.slb.matched.map((m, i) => (
                          <tr key={i}>
                            <Td>{fmtDate(m.first_date)} (−{fmtQty(m.first_qty)})</Td>
                            <Td>→</Td>
                            <Td>{fmtDate(m.return_date)} (+{fmtQty(m.return_qty)})</Td>
                            <Td align="right" mute={!m.exact_match}>
                              {m.exact_match ? '✓ exact' : '~ approx'}
                            </Td>
                          </tr>
                        ))}
                      </tbody>
                    </CodeTable>
                  </div>
                )}
                {trace.slb.unmatched_firsts.length > 0 && (
                  <div>
                    <div className="text-[11px] uppercase tracking-wider mb-1"
                         style={{ color: 'var(--status-info)' }}>
                      Unmatched first legs (still out on loan)
                    </div>
                    <CodeTable>
                      <tbody>
                        {trace.slb.unmatched_firsts.map(u => (
                          <tr key={u.pair_id}>
                            <Td>{fmtDate(u.date)}</Td>
                            <Td className="truncate" mute>SLB FIRST LEG</Td>
                            <Td align="right" width="80px">{fmtQty(u.qty)}</Td>
                          </tr>
                        ))}
                        <tr style={{ borderTop: '1px dashed var(--glass-border)' }}>
                          <Td colSpan={2} bold>slb_outstanding =</Td>
                          <Td align="right" bold>{fmtQty(trace.slb.outstanding)}</Td>
                        </tr>
                      </tbody>
                    </CodeTable>
                  </div>
                )}
              </div>
            )}
          </Section>

          {/* ─── 4. Status decision ────────────────────────────────── */}
          <Section title="4 · Status decision">
            <div
              className="rounded-md px-3 py-3 font-mono text-xs"
              style={{
                background: `color-mix(in srgb, ${statusColor} 10%, transparent)`,
                color: statusColor,
              }}
            >
              {trace.status_reason}
            </div>
          </Section>

        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t flex justify-end"
             style={{ borderColor: 'var(--glass-border)' }}>
          <button
            type="button"
            onClick={onClose}
            className="text-sm px-4 py-1.5 rounded border border-axis-cloud-200"
            style={{ color: 'var(--text-strong)' }}
          >Close</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Small UI helpers — kept inline to avoid a new component file ──── */

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider font-semibold mb-2"
           style={{ color: 'var(--text-200)' }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function CodeTable({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md overflow-hidden border" style={{ borderColor: 'var(--glass-border)' }}>
      <table className="w-full text-xs font-mono">{children}</table>
    </div>
  );
}

function Th({ children, align }: { children: React.ReactNode; align?: 'right' }) {
  return (
    <th
      className={`px-2.5 py-1.5 text-[10px] uppercase tracking-wider font-semibold ${align === 'right' ? 'text-right' : 'text-left'}`}
      style={{ background: 'var(--bg-elevated)', color: 'var(--text-200)' }}
    >{children}</th>
  );
}

function Td({
  children, align, width, colSpan, bold, italic, mute, tone, className,
}: {
  children: React.ReactNode;
  align?: 'right';
  width?: string;
  colSpan?: number;
  bold?: boolean;
  italic?: boolean;
  mute?: boolean;
  tone?: 'ok' | 'warn' | 'mute';
  className?: string;
}) {
  const toneColor =
    tone === 'ok'   ? 'var(--status-ok)'
  : tone === 'warn' ? 'var(--status-warn)'
  : tone === 'mute' || mute ? 'var(--text-200)'
  : undefined;
  return (
    <td
      colSpan={colSpan}
      style={{
        width,
        color: toneColor,
        fontWeight: bold ? 600 : undefined,
        fontStyle: italic ? 'italic' : undefined,
      }}
      className={`px-2.5 py-1 ${align === 'right' ? 'text-right tabular-nums' : ''} ${className ?? ''}`}
    >{children}</td>
  );
}

function Note({ children }: { children: React.ReactNode }) {
  return <div className="text-xs italic" style={{ color: 'var(--text-200)' }}>{children}</div>;
}

function fmtQty(n: number): string {
  return Number.isInteger(n) ? n.toString() : n.toFixed(2);
}

function fmtSigned(n: number): string {
  if (n === 0) return '0';
  return (n > 0 ? '+' : '') + fmtQty(n);
}

/** Strip the time component off a serialized date so the math modal
 *  shows '2026-05-19' instead of '2026-05-19T00:00:00.000Z'. */
function fmtDate(d: string | null | undefined): string {
  if (!d) return '—';
  return d.length >= 10 ? d.slice(0, 10) : d;
}
